// New quote's line picker (#2219): the builder's catalog search, driven from the keyboard. Typing narrows the dealer's
// priced catalog that is already on the page; the best match that can be added is highlighted, Up and Down move, Enter
// adds and Escape closes. It prices nothing and decides no eligibility: every row is the dealer's catalog as Omni sent
// it, and a chosen product goes to the builder's own `add`. A dealer is shown only what it can quote, as on its Catalog
// page. Phoneware staff searching for a dealer also see the matches that dealer can't quote, with the catalog's reason,
// as they do on a quote document's review. Typing makes no request.
import React, { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatCents, type DealerModel, type PricedProduct, type QuoteLine } from "@phoneware/lens-domain";
import { BILLING, plural } from "../format.js";
import { Icon } from "../icons.js";
import { Callout } from "../ui.js";

/** Rows on show before Show all. */
const PICK_ROWS = 8;

/** What the picker works from: the dealer's priced catalog, or why there is none on the page yet. */
export type PickerSource =
  | { kind: "ready"; catalog: PricedProduct[] }
  | { kind: "loading" }
  | { kind: "customer" }
  | { kind: "error"; message: string; retrying: boolean };

interface Match {
  product: PricedProduct;
  /** Why this row can't go on the quote; null when it can. */
  blocked: string | null;
}

const NONE: PricedProduct[] = [];

/** A line carries whole cents. Anything else is no price, never $0.00. */
function hasPrice(p: PricedProduct): boolean {
  return Number.isInteger(p.listPrice) && p.listPrice >= 0;
}

/** The catalog's own reason a dealer can't quote it, or the missing price. */
function blockedBy(p: PricedProduct): string | null {
  if (!p.quotable) return p.notQuotableReason ?? "This dealer can't quote it right now.";
  return hasPrice(p) ? null : "No price for this dealer.";
}

/**
 * Where a product ranks for what was typed, best first, or null when it does not match: its exact code, its exact
 * name, a code starting with it, a name starting with it, a word in the name starting with it, then anywhere in the
 * name or code, then the category or description as well (the search the builder always had).
 */
function rank(p: PricedProduct, q: string): number | null {
  const code = p.code.trim().toLowerCase();
  const name = p.name.trim().toLowerCase();
  if (code === q) return 0;
  if (name === q) return 1;
  if (code.startsWith(q)) return 2;
  if (name.startsWith(q)) return 3;
  if (name.split(/[^a-z0-9]+/).some((word) => word.startsWith(q))) return 4;
  if (code.includes(q) || name.includes(q)) return 5;
  if (`${p.name} ${p.code} ${p.category} ${p.description}`.toLowerCase().includes(q)) return 6;
  return null;
}

interface Found {
  /** The rows to list, best match first. */
  rows: Match[];
  /**
   * Every product in the dealer's catalog whose code or whole name is exactly what was typed: in any category, listed or
   * not, addable or not. The highlight may only guess a product that is the one exact match, or guess when there is none.
   */
  exact: PricedProduct[];
}

/**
 * The rows for what was typed. Browsing with nothing typed lists what this dealer can add, in the catalog's order. A
 * search lists its matches best first: between equal matches, what can be added first, then the catalog's order. A match
 * the dealer can't add is listed only when `showBlocked` (Phoneware staff); a dealer's own people never see it.
 */
function searchCatalog(catalog: PricedProduct[], query: string, category: string, showBlocked: boolean): Found {
  const q = query.trim().toLowerCase();
  const ranked = catalog.flatMap((product, at) => {
    if (category && product.category !== category) return [];
    const blocked = blockedBy(product);
    if (blocked !== null && !(q && showBlocked)) return [];
    const tier = q ? rank(product, q) : 0;
    return tier === null ? [] : [{ product, blocked, at, tier }];
  });
  ranked.sort((a, b) => a.tier - b.tier || Number(a.blocked !== null) - Number(b.blocked !== null) || a.at - b.at);
  const exact = q ? catalog.filter((p) => p.code.trim().toLowerCase() === q || p.name.trim().toLowerCase() === q) : [];
  return { rows: ranked.map(({ product, blocked }) => ({ product, blocked })), exact };
}

/** The typed text, marked where it matched. */
function Marked({ text, q }: { text: string; q: string }): React.ReactElement {
  const lower = text.toLowerCase();
  const at = q && lower.length === text.length ? lower.indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark>{text.slice(at, at + q.length)}</mark>
      {text.slice(at + q.length)}
    </>
  );
}

function Option({
  id,
  match,
  q,
  current,
  onQuote,
  onChoose,
}: {
  id: string;
  match: Match;
  q: string;
  current: boolean;
  onQuote: number;
  onChoose: (p: PricedProduct) => void;
}): React.ReactElement {
  const p = match.product;
  const blocked = match.blocked;
  const price = hasPrice(p) ? formatCents(p.listPrice) : null;
  const per = p.billing === "monthly" ? "a month" : "once";
  const basis = [p.category, [BILLING[p.billing].toLowerCase(), p.unit].filter(Boolean).join(", ")].filter(Boolean).join(" \u00b7 ");
  const guide = p.floorPrice !== null ? `floor ${formatCents(p.floorPrice)}` : p.buyPrice !== null ? `buy ${formatCents(p.buyPrice)}` : null;
  return (
    <li
      id={id}
      role="option"
      className="pick-opt"
      aria-selected={current}
      aria-disabled={blocked ? true : undefined}
      // No aria-label: the option is named by what it shows, in order (name, code, category and billing, the reason it
      // can't be quoted or how many are on the quote, price, period, floor or buy price), so the two never drift.
      data-testid={blocked ? `quote-product-blocked-${p.id}` : `quote-product-option-${p.id}`}
      // The search keeps focus while a row is clicked or tapped.
      onMouseDown={(e) => e.preventDefault()}
      onClick={blocked ? undefined : () => onChoose(p)}
    >
      <span className="pick-main">
        <span className="pick-name">
          <Marked text={p.name} q={q} />
          {p.code ? (
            <>
              {" "}
              <span className="code">
                <Marked text={p.code} q={q} />
              </span>
            </>
          ) : null}
        </span>{" "}
        <span className="pick-meta">{basis}</span>{" "}
        {blocked ? (
          <span className="pick-why">
            <Icon name="ban" size={12} />
            {blocked}
          </span>
        ) : onQuote ? (
          <span className="pick-have">
            <Icon name="check" size={12} />
            {onQuote} on the quote
          </span>
        ) : null}
      </span>{" "}
      <span className="amount pick-price">
        {price ?? "No price"}
        {price ? (
          <>
            {" "}
            <small className="pick-per">{per}</small>
          </>
        ) : null}
        {price && guide ? (
          <>
            {" "}
            <small>{guide}</small>
          </>
        ) : null}
      </span>
      {blocked ? null : (
        <span className="btn small pick-add" aria-hidden="true">
          <Icon name="plus" size={14} />
          <span className="pick-add-l">Add</span>
        </span>
      )}
    </li>
  );
}

export function QuoteLinePicker({
  source,
  model,
  dealerName,
  showBlocked,
  lines,
  onAdd,
  onRetry,
}: {
  source: PickerSource;
  model: DealerModel;
  /** Phoneware staff quote for a customer's dealer: the dealer whose prices these are. Null for a dealer's own people. */
  dealerName: string | null;
  /**
   * Phoneware staff who read the whole catalog also see a search's matches this dealer can't quote, dimmed, with the
   * catalog's reason. A dealer's own people never do: they are shown only what they can add, as on their Catalog page.
   */
  showBlocked: boolean;
  lines: QuoteLine[];
  onAdd: (p: PricedProduct) => void;
  onRetry: () => void;
}): React.ReactElement {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  // The results sit in the card, as the catalog list always has; Escape puts them away until the search is used again.
  const [open, setOpen] = useState(true);
  const [all, setAll] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const keepInView = useRef(false);
  const uid = useId();
  const listId = `${uid}-list`;
  const keysId = `${uid}-keys`;

  const catalog = source.kind === "ready" ? source.catalog : NONE;
  const addableAll = useMemo(() => catalog.filter((p) => blockedBy(p) === null), [catalog]);
  const categories = useMemo(() => [...new Set(addableAll.map((p) => p.category))].sort(), [addableAll]);
  const cat = categories.includes(category) ? category : "";
  const found = useMemo(() => searchCatalog(catalog, query, cat, showBlocked), [catalog, query, cat, showBlocked]);
  const matches = found.rows;
  const onQuote = useMemo(() => new Map(lines.map((l) => [l.productId, l.qty])), [lines]);

  const q = query.trim();
  const ready = source.kind === "ready" && addableAll.length > 0;
  const addable = matches.filter((m) => m.blocked === null);
  const blockedCount = matches.length - addable.length;
  const shown = all ? matches : matches.slice(0, PICK_ROWS);
  const choosable = shown.filter((m) => m.blocked === null);
  // The row Enter adds: the one chosen with the arrows, or last added, while it is on show. Otherwise, once something is
  // typed, the best match on show that can be added, but only when no other product has exactly the code or name typed.
  // Typing one product's code never highlights another one, so Enter then waits for an arrow. With nothing typed, or
  // when no row on show can be added, nothing is highlighted until Down or Up.
  const best = q ? (choosable[0] ?? null) : null;
  const guess = best && found.exact.every((p) => p.id === best.product.id) ? best : null;
  const current = choosable.find((m) => m.product.id === active) ?? guess;
  const expanded = ready && open && shown.length > 0;
  const currentId = expanded && current ? `${uid}-${current.product.id}` : undefined;

  useLayoutEffect(() => {
    if (currentId) document.getElementById(currentId)?.scrollIntoView?.({ block: "nearest" });
  }, [currentId]);

  // A line added from the keyboard lands above the picker and pushes it down: keep the search on screen.
  useLayoutEffect(() => {
    if (!keepInView.current) return;
    keepInView.current = false;
    input.current?.scrollIntoView?.({ block: "nearest" });
  }, [lines]);

  function search(next: string): void {
    setQuery(next);
    setActive(null);
    setAll(false);
    setOpen(true);
  }

  function filter(next: string): void {
    setCategory(next);
    setActive(null);
    setAll(false);
    setOpen(true);
  }

  function choose(p: PricedProduct): void {
    onAdd(p);
    setActive(p.id);
    if (input.current && document.activeElement === input.current) {
      // The next keystroke starts a new search; Enter again adds one more of the same.
      input.current.select();
      keepInView.current = true;
    }
  }

  function move(down: boolean): void {
    // Every row on show is one this dealer can't quote, which only Phoneware staff see: bring in the rest and choose the
    // first row that can be added on Down, or the last on Up. The rows that can't be added are still never chosen.
    if (!choosable.length) {
      if (!addable.length) return;
      setAll(true);
      setActive(addable[down ? 0 : addable.length - 1].product.id);
      return;
    }
    const at = current ? choosable.indexOf(current) : -1;
    if (!down) {
      setActive(choosable[at <= 0 ? choosable.length - 1 : at - 1].product.id);
      return;
    }
    // Down from the last row on show brings in the rest rather than wrapping past them.
    if (at === choosable.length - 1 && !all && addable.length > choosable.length) {
      setAll(true);
      setActive(addable[choosable.length].product.id);
      return;
    }
    setActive(choosable[(at + 1) % choosable.length].product.id);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!ready) return;
      if (!open) {
        // Opening the results chooses nothing new when a row is already highlighted; otherwise the first or last row that
        // can be added, as Down or Up on open results would.
        setOpen(true);
        if (!current) move(e.key === "ArrowDown");
        return;
      }
      move(e.key === "ArrowDown");
    } else if (e.key === "Enter") {
      // Enter adds the highlighted row and does nothing else: never a save, a send or a form submit.
      e.preventDefault();
      if (expanded && current) choose(current.product);
    } else if (e.key === "Escape") {
      if (ready && open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      } else if (query) {
        e.preventDefault();
        e.stopPropagation();
        setQuery("");
        setActive(null);
        setAll(false);
      }
    }
  }

  const note =
    dealerName === null
      ? model === "agent"
        ? "At Phoneware list price"
        : "At your sell price"
      : model === "agent"
        ? `At Phoneware list price for ${dealerName}`
        : `At the sell price for ${dealerName}`;

  let body: React.ReactNode = null;
  if (source.kind === "loading") {
    body = <div className="empty">Loading the catalog.</div>;
  } else if (source.kind === "customer") {
    body = <div className="empty">Pick the customer first.</div>;
  } else if (source.kind === "error") {
    body = (
      <Callout kind="stop" icon="triangle-alert">
        The catalog did not load: {source.message}{" "}
        <button className="link" type="button" disabled={source.retrying} data-testid="quote-catalog-retry" onClick={onRetry}>
          Try again
        </button>
      </Callout>
    );
  } else if (!ready) {
    body = <div className="empty">Nothing to quote yet. Phoneware adds products from Autotask, and they show up here when they are ready to quote.</div>;
  } else if (open && !matches.length) {
    body = (
      <div className="empty" data-testid="quote-product-none">
        {cat ? (
          <>
            Nothing in {cat} matches &ldquo;{q}&rdquo;.{" "}
            <button className="link" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => filter("")}>
              Search every category
            </button>
          </>
        ) : (
          <>Nothing in the catalog matches &ldquo;{q}&rdquo;.</>
        )}
      </div>
    );
  }

  const status = !expanded
    ? body && open && ready
      ? "No matches."
      : ""
    : `${plural(addable.length, "match", "matches")}${blockedCount ? `, ${blockedCount} can\u2019t be quoted` : ""}.`;

  return (
    <section className="card picker" data-testid="quote-picker">
      <div className="card-h">
        <h2>Add from the catalog</h2>
        <span className="note">{note}</span>
      </div>
      <div className="pick-bar">
        <label className="search grow">
          <Icon name="search" size={15} />
          <span className="sr">Search the catalog</span>
          <input
            ref={input}
            className="input"
            type="search"
            role="combobox"
            autoComplete="off"
            spellCheck={false}
            placeholder="Search by name or code"
            aria-expanded={expanded}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={currentId}
            aria-describedby={ready ? keysId : undefined}
            disabled={source.kind === "customer"}
            data-testid="quote-product-search"
            value={query}
            onChange={(e) => search(e.target.value)}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
        </label>
        {ready && categories.length > 1 ? (
          <div className="seg" role="group" aria-label="Category">
            {["", ...categories].map((c) => (
              <button key={c || "all"} type="button" aria-pressed={cat === c} onMouseDown={(e) => e.preventDefault()} onClick={() => filter(c)}>
                {c || "All"}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {ready ? (
        <p className="pick-keys" id={keysId}>
          <kbd>&uarr;</kbd> <kbd>&darr;</kbd> to choose, <kbd>Enter</kbd> to add, <kbd>Esc</kbd> to close
        </p>
      ) : null}
      <ul className={all ? "pick-list all" : "pick-list"} id={listId} role="listbox" aria-label="Catalog matches" hidden={!expanded} data-testid="quote-product-list">
        {expanded
          ? shown.map((m) => (
              <Option
                key={m.product.id}
                id={`${uid}-${m.product.id}`}
                match={m}
                q={q}
                current={m === current}
                onQuote={onQuote.get(m.product.id) ?? 0}
                onChoose={choose}
              />
            ))
          : null}
      </ul>
      {body}
      {expanded && matches.length > PICK_ROWS ? (
        <div className="pick-more">
          <span>{all ? `Showing all ${matches.length}.` : `Showing ${PICK_ROWS} of ${matches.length}.`}</span>
          <button className="link" type="button" data-testid="quote-product-more" onMouseDown={(e) => e.preventDefault()} onClick={() => setAll(!all)}>
            {all ? "Show fewer" : `Show all ${matches.length}`}
          </button>
        </div>
      ) : null}
      <p className="sr" role="status" aria-live="polite">
        {status}
      </p>
    </section>
  );
}
